import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { cn } from '@bem-react/classname'

import { Memo } from '@/hoc/Memo'
import { loadingSelector } from '@/core/services/Loading/store/loading.selector'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'

import './TermsPage.scss'

const cnTerms = cn('Terms')

export const TermsPage: FC = Memo(() => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isLoading = useAppSelector(loadingSelector)

  useEffect(() => {
    if (isLoading) {
      dispatch(changeLoading(false))
    }
  }, [isLoading])

  return (
    <>
      <Helmet>
        <title>{t('Terms of Service')} | DTM</title>
      </Helmet>
      <div className={cnTerms()}>
        <div className={cnTerms('text')}>
          <div className={cnTerms('title')}>
            {t('Terms and Conditions of ')}
            <b>{t('registration.dinamicagenerale.com')}</b>
          </div>
          <div>{t('These Terms govern')}</div>
          <ul>
            <li>{t('the use of this Application, and,')}</li>
            <li>
              {t(
                'any other related Agreement or legal relationship with the Owner',
              )}
            </li>
          </ul>
          <div>
            {t(
              'in a legally binding way. Capitalized words are defined in the relevant dedicated section of this document.',
            )}
          </div>
          <div>{t('The User must read this document carefully.')}</div>

          <hr className={cnTerms('border')} />
          <div>
            {t(
              'Although the entire contractual relationship relating to these Products is entered into solely by the Owner and Users, Users acknowledge and agree that, where this Application has been provided to them via the Apple App Store, Apple may enforce these Terms as a third-party beneficiary.',
            )}
          </div>
          <div>{t('This Application is provided by')}</div>
          <div>{t('Dinamica Generale S.p.A.')}</div>
          <div>{t('Via Mondadori, 15 - 46025 - Poggio Rusco (MN)')}</div>
          <div>{t('company registration number 01599500202')}</div>
          <div>{t('REA MN 169392')}</div>
          <div>{t('Paid-in capital 1.000.000,00 I.V.')}</div>
          <div>{t('Owner contact email: privacy@dinamicagenerale.com')}</div>
          <div>{t('"This Application" refers to')}</div>
          <ul>
            <li>
              {t(
                'this website, including its subdomains and any other website through which the Owner makes its Service available;',
              )}
            </li>
            <li>
              {t(
                'applications for mobile, tablet and other smart device systems;',
              )}
            </li>
            <li>{t('the Application Program Interfaces (API);')}</li>
            <li>{t('the Service;')}</li>
            <li>
              {t(
                'any applications, sample and content files, source code, scripts, instruction sets or software included as part of the Service, as well as any related documentation;',
              )}
            </li>
          </ul>
          <div>
            {t(
              'The following documents are incorporated by reference into these Terms:',
            )}
          </div>
          <div className={cnTerms('header')}>
            {t('What the User should know at a glance')}
          </div>
          <ul>
            <li>
              {t(
                'Please note that some provisions in these Terms may only apply to certain categories of Users. In particular, certain provisions may only apply to Consumers or to those Users that do not qualify as Consumers. Such limitations are always explicitly mentioned within each affected clause. In the absence of any such mention, clauses apply to all Users.',
              )}
            </li>
            <li>
              {t(
                'The right of withdrawal only applies to European Consumers. The right of withdrawal, also commonly called the right of cancellation in the UK, is consistently referred to as “the right of withdrawal” within this document.',
              )}
            </li>
            <li>
              {t(
                'This Application uses automatic renewal for Product subscriptions. Information about the a) renewal period, b) termination details and c) termination notice can be found in the relevant section of these Terms.',
              )}
            </li>
          </ul>
          <hr className={cnTerms('border')} />
          <div className={cnTerms('header')}>{t('TERMS OF USE')}</div>
          <div>
            {t(
              'Unless otherwise specified, the terms of use detailed in this section apply generally when using this Application.',
            )}
          </div>
          <div>
            {t(
              'Single or additional conditions of use or access may apply in specific scenarios and in such cases are additionally indicated within this document.',
            )}
          </div>
          <div>
            {t(
              'By using this Application, Users confirm to meet the following requirements:',
            )}
          </div>
          <ul>
            <li>
              {t(
                'There are no restrictions for Users in terms of being Consumers or Business Users;',
              )}
            </li>
            <li>
              {t(
                'Users aren’t located in a country that is subject to a U.S. Government embargo, or that has been designated by the U.S. Government as a “terrorist-supporting” country;',
              )}
            </li>
            <li>
              {t(
                'Users aren’t listed on any U.S. Government list of prohibited or restricted parties;',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>
            {t('Account registration')}
          </div>
          <div>
            {t(
              'To use the Service Users must register or create a User account, providing all required data or information in a complete and truthful manner.Failure to do so will cause unavailability of the Service.',
            )}
          </div>
          <div>
            {t(
              'Users are responsible for keeping their login credentials confidential and safe. For this reason, Users are also required to choose passwords that meet the highest standards of strength permitted by this Application.',
            )}
          </div>
          <div>
            {t(
              'By registering, Users agree to be fully responsible for all activities that occur under their username and password.Users are required to immediately and unambiguously inform the Owner via the contact details indicated in this document, if they think their personal information, including but not limited to User accounts, access credentials or personal data, have been violated, unduly disclosed or stolen.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Conditions for account registration')}
          </div>
          <div>
            {t(
              'Registration of User accounts on this Application is subject to the conditions outlined below. By registering, Users agree to meet such conditions.',
            )}
          </div>
          <ul>
            <li>
              {t(
                'Accounts registered by bots or any other automated methods are not permitted.',
              )}
            </li>
            <li>
              {t(
                'Unless explicitly permitted, a User account may not be shared with other persons.',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>{t('Account termination')}</div>
          <div>
            {t(
              'Users can terminate their account and stop using the Service at any time by doing the following:',
            )}
          </div>
          <ul>
            <li>
              {t(
                'By directly contacting the Owner at the contact details provided in this document.',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>
            {t('Account suspension and deletion')}
          </div>
          <div>
            {t(
              'The Owner reserves the right, at its sole discretion, to suspend or delete at any time and without notice, User accounts which it deems inappropriate, offensive or in violation of these Terms.',
            )}
          </div>
          <div>
            {t(
              'The suspension or deletion of User accounts shall not entitle Users to any claims for compensation, damages or reimbursement.',
            )}
          </div>
          <div>
            {t(
              'The suspension or deletion of accounts due to causes attributable to the User does not exempt the User from paying any applicable fees or prices.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Content on this Application')}
          </div>
          <div>
            {t(
              'Unless where otherwise specified or clearly recognizable, all content available on this Application is owned or provided by the Owner or its licensors.',
            )}
          </div>
          <div>
            {t(
              'The Owner undertakes its utmost effort to ensure that the content provided on this Application infringes no applicable legal provisions or third-party rights. However, it may not always be possible to achieve such a result.In such cases, without prejudice to any legal prerogatives of Users to enforce their rights, Users are kindly asked to preferably report related complaints using the contact details provided in this document.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'Rights regarding content on this Application - All rights reserved',
            )}
          </div>
          <div>
            {t(
              'The Owner holds and reserves all intellectual property rights for any such content.',
            )}
          </div>
          <div>
            {t(
              'Users may not therefore use such content in any way that is not necessary or implicit in the proper use of the Service.',
            )}
          </div>
          <div>
            {t(
              'In particular, but without limitation, Users may not copy, download, share (beyond the limits set forth below), modify, translate, transform, publish, transmit, sell, sublicense, edit, transfer/assign to third parties or create derivative works from the content available on this Application, nor allow any third party to do so through the User or their device, even without the Users knowledge.',
            )}
          </div>
          <div>
            {t(
              'Where explicitly stated on this Application, the User may download, copy and/or share some content available through this Application for its sole personal and non-commercial use and provided that the copyright attributions and all the other attributions requested by the Owner are correctly implemented.',
            )}
          </div>
          <div>
            {t(
              'Any applicable statutory limitation or exception to copyright shall stay unaffected.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Access to provided content')}
          </div>
          <div>
            {t(
              'Content that Users provide to this Application is made available according to the criteria outlined within this section.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Private content')}</div>
          <div>
            {t(
              'Private content provided by Users shall stay private and will not be shared with any third parties or accessed by the Owner without the User’s explicit consent.',
            )}
          </div>
          <div>
            {t(
              'Users may (and are encouraged to) check on this Application to find details of who can access the content they provide.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Access to external resources')}
          </div>
          <div>
            {t(
              'Through this Application Users may have access to external resources provided by third parties. Users acknowledge and accept that the Owner has no control over such resources and is therefore not responsible for their content and availability.',
            )}
          </div>
          <div>
            {t(
              'Conditions applicable to any resources provided by third parties, including those applicable to any possible grant of rights in content, result from each such third parties’ terms and conditions or, in the absence of those, applicable statutory law.',
            )}
          </div>
          <div className={cnTerms('header')}>
            {t('How to file a takedown notice (called a “DMCA notice”)')}
          </div>
          <div>
            {t(
              'If copyright holders or their agents believe that any content on this Application infringes upon their copyrights, they may submit a notification pursuant to the Digital Millennium Copyright Act ("DMCA") by providing the Owner’s Copyright Agent with the following information in writing (see 17 U.S.C 512(c)(3) for further detail)',
            )}
          </div>
          <ul>
            <li>
              {t(
                'A physical or electronic signature of a person authorized to act on behalf of the holder of an exclusive right that is allegedly infringed;',
              )}
            </li>
            <li>
              {t(
                'Identification of the copyrighted work claimed to have been infringed, or, if multiple copyrighted works at a single online site are covered by a single notification, a representative list of such works at that site;',
              )}
            </li>
            <li>
              {t(
                'Identification of the material that is claimed to be infringing or to be the subject of infringing activity and that is to be removed or access to which is to be disabled and information reasonably sufficient to permit the Owner to locate the material;',
              )}
            </li>
            <li>
              {t(
                'Information reasonably sufficient to permit the Owner to contact the notifying party, such as an address, telephone number, and, if available, an electronic mail;',
              )}
            </li>
            <li>
              {t(
                'A statement that the notifying party has a good faith belief that use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law; and',
              )}
            </li>
            <li>
              {t(
                'A statement that the information in the notification is accurate, and under penalty of perjury, that the notifying party is authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.',
              )}
            </li>
          </ul>
          <div>
            {t(
              'Failure to comply with all of the requirements outlined above may result in invalidity of the DMCA notice.',
            )}
          </div>
          <div>
            {t(
              'Copyright infringement notifications may be addressed to the Owner’s Copyright Agent at the contact details specified in this document.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Acceptable use')}</div>
          <div>
            {t(
              'This Application and the Service may only be used within the scope of what they are provided for, under these Terms and applicable law.',
            )}
          </div>
          <div>
            {t(
              'Users are solely responsible for making sure that their use of this Application and/or the Service violates no applicable law, regulations or third-party rights.',
            )}
          </div>
          <div>
            {t(
              'Therefore, the Owner reserves the right to take any appropriate measure to protect its legitimate interests including denying Users access to this Application or the Service, terminating contracts, reporting any misconduct performed through this Application or the Service to the competent authorities – such as judicial or administrative authorities - whenever Users are suspected to be in violation of any laws, regulations, third-party rights and/or these Terms, including, but not limited to, by engaging in any of the following activities',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Conduct restrictions')}
          </div>
          <ul>
            <li>
              {t(
                'pretending to fulfill any possible condition or requirements for accessing this Application and/or using the Services, such as for instance being adult according to law or qualifying as a Consumer;',
              )}
            </li>
            <li>
              {t(
                'concealing their identity or stealing someone elses identity or pretend to be or represent a third-party, if not allowed to do so by such third-party;',
              )}
            </li>
            <li>
              {t(
                'manipulating identifiers to disguise or otherwise conceal the origin of their messages or of the content posted;',
              )}
            </li>
            <li>
              {t(
                'defaming, abusing, harassing, using threatening practices, threatening or violating the legal rights of others in any other way;',
              )}
            </li>
            <li>
              {t(
                'promoting activity that may endanger the User’s life or the life of any other User or lead to physical harm. This includes but is not limited to suicide threats or instigations, intentional physical trauma, the use of illegal drugs, or excessive drinking. Under no circumstance is any User allowed to post any content promoting and/or encouraging and/or showing any self-destructive or violent behavior on this Application;',
              )}
            </li>
            <li>
              {t(
                'probing, scanning or testing the vulnerability of this Application, including the services or any network connected to the website, nor breaching the security or authentication measures on this Application, including the services or any network connected to this Application;',
              )}
            </li>
            <li>
              {t(
                'installing, embedding, uploading or otherwise incorporating any malware into or via this Application;',
              )}
            </li>
            <li>
              {t(
                'using this Application or the technical infrastructure in an abusive, excessive or otherwise inappropriate way (for example: for spamming purposes);',
              )}
            </li>
            <li>
              {t(
                'attempting to disrupt or tamper with the technical infrastructure in a manner that harms or places an undue burden on this Application or the Service;',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>{t('Scraping')}</div>
          <ul>
            <li>
              {t(
                'adopting any automated process to extract, harvest or scrape information, data and/or content from this Application and all the digital properties thereto related unless where explicitly allowed to do so by the Owner;',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>
            {t('Content restrictions')}
          </div>
          <ul>
            <li>
              {t(
                'disseminating or publishing content that is unlawful, obscene, illegitimate, libelous or inappropriate;',
              )}
            </li>
            <li>
              {t(
                'publishing any content that promotes, either directly or indirectly, hate, racism, discrimination, pornography, violence;',
              )}
            </li>
            <li>
              {t(
                'disseminating or publishing any content that is false or may create unjustified alarm;',
              )}
            </li>
            <li>
              {t(
                'using this Application to publish, disseminate or otherwise provide content protected by intellectual property laws, including but not limited to patent, trademark or copyright law, unlawfully and without the legitimate right-holder’s consent;',
              )}
            </li>
            <li>
              {t(
                'using this Application to publish, disseminate or otherwise make available any other content which infringes on any third-party rights, including but not limited to state, military, trade or professional secrets and personal data;',
              )}
            </li>
            <li>
              {t(
                'publishing any content or carrying out any activity that disrupts, interrupts, harms, or otherwise violates the integrity of this Application or another Users experience or devices. Such activities include: spamming, distributing unauthorized advertisements, phishing, defrauding others, spreading malware or viruses etc.;',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>{t('User protection')}</div>
          <ul>
            <li>{t('misappropriating any account in use by another User;')}</li>
            <li>
              {t(
                'harvesting or collecting any personally identifying information of other Users including but not limited to their email addresses or contact information, by circumventing the privacy setting of other Users’ accounts on this Application or by any other means;',
              )}
            </li>
            <li>
              {t(
                'using any information relating to other Users, including personal or contact data, for purposes other than those this Application is intended for;',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>{t('Software license')}</div>
          <div>
            {t(
              'Any intellectual or industrial property rights, and any other exclusive rights on software or technical applications embedded in or related to this Application are held by the Owner and/or its licensors.',
            )}
          </div>
          <div>
            {t(
              'Subject to Users’ compliance with and notwithstanding any divergent provision of these Terms, the Owner merely grants Users a revocable, non-exclusive, non-sublicensable and non-transferable license to use the software and/or any other technical means embedded in the Service within the scope and for the purposes of this Application and the Service offered.',
            )}
          </div>
          <div>
            {t(
              'This license does not grant Users any rights to access, usage or disclosure of the original source code. All techniques, algorithms, and procedures contained in the software and any documentation thereto related is the Owner’s or its licensors’ sole property.',
            )}
          </div>
          <div>
            {t(
              'All rights and license grants to Users shall immediately terminate upon any termination or expiration of the Agreement.',
            )}
          </div>
          <div>
            {t(
              'Without prejudice to the above, under this license Users may download, install, use and run the software on the permitted number of devices, provided that such devices are common and up-to-date in terms of technology and market standards.',
            )}
          </div>
          <div>
            {t(
              'The Owner reserves the right to release updates, fixes and further developments of this Application and/or its related software and to provide them to Users for free. Users may need to download and install such updates to continue using this Application and/or its related software.',
            )}
          </div>
          <div>
            {t('New releases may only be available against payment of a fee.')}
          </div>
          <div>
            {t(
              'The User may download, install, use and run the software on unlimited devices.',
            )}
          </div>
          <div>
            {t(
              'However, it may not be permitted to run the software on more than one device at a time.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('API usage terms')}</div>
          <div>
            {t(
              'Users may access their data relating to this Application via the Application Program Interface (API). Any use of the API, including use of the API through a third-party product/service that accesses this Application, is bound by these Terms and, in addition, by the following specific terms:',
            )}
          </div>
          <ul>
            <li>
              {t(
                'the User expressly understands and agrees that the Owner bears no responsibility and shall not be held liable for any damages or losses resulting from the User’s use of the API or their use of any third-party products/services that access data through the API.',
              )}
            </li>
          </ul>
          <div className={cnTerms('header')}>
            {t('TERMS AND CONDITIONS OF SALE')}
          </div>
          <div className={cnTerms('subheader')}>{t('Paid Products')}</div>
          <div>
            {t(
              'Some of the Products provided on this Application, as part of the Service, are provided on the basis of payment.',
            )}
          </div>
          <div>
            {t(
              'The fees, duration and conditions applicable to the purchase of such Products are described below and in the dedicated sections of this Application.',
            )}
          </div>
          <div>
            {t(
              'To purchase Products, the User must register or log into this Application.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Product description')}</div>
          <div>
            {t(
              'Prices, descriptions or availability of Products are outlined in the respective sections of this Application and are subject to change without notice.',
            )}
          </div>
          <div>
            {t(
              'While Products on this Application are presented with the greatest accuracy technically possible, representation on this Application through any means (including, as the case may be, graphic material, images, colors, sounds) is for reference only and implies no warranty as to the characteristics of the purchased Product.',
            )}
          </div>
          <div>
            {t(
              'The characteristics of the chosen Product will be outlined during the purchasing process.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Purchasing process')}</div>
          <div>
            {t(
              'Any steps needed from Product choice to order submission, form part of the purchasing process.The purchasing process includes these steps',
            )}
          </div>
          <ul>
            <li>
              {t(
                'Users must indicate the desired Product by selecting it, including, where possible, quantity and specific characteristics, to make it appear in the purchase selection.',
              )}
            </li>
            <li>
              {t(
                'Users may review their purchase selection, modify, remove or add items.',
              )}
            </li>
            <li>
              {t(
                'Users will be required to specify their billing address, contact details and a payment method of their choice.',
              )}
            </li>
            <li>
              {t(
                'If the purchase results in a product being shipped, Users may need to indicate a shipping address.',
              )}
            </li>
            <li>
              {t(
                'During the purchasing process, Users may, at any time, modify, correct or change the information provided, or altogether abort the purchasing process with no consequence.',
              )}
            </li>
            <li>
              {t(
                'After providing all required information, Users must carefully review the order and, subsequently, may proceed to checkout.',
              )}
            </li>
            <li>
              {t(
                'By clicking on the checkout button, Users open the Stripe checkout view wherein they will be required to specify their contact details and a payment method of their choice.',
              )}
            </li>
            <li>
              {t(
                'Users, who have not already done so, may ask Stripe to store their payment information for future purchases on this Application or sites that use Stripe as a payment gateway. The User may read the privacy policy of Stripe and this Application to learn more about the data processing and User rights regarding their data.',
              )}
            </li>
            <li>
              {t(
                'Within the Stripe checkout view, Users may be requested to also specify their billing and shipping address.',
              )}
            </li>
            <li>
              {t(
                'Within the Stripe checkout view, Users will be given the possibility to choose the express checkout. The express checkout allows Users to finalize the purchase directly, making use of payment and contact information stored by common online payment processing services (such as “ApplePay”, “Google Pay”, “Microsoft Pay”).',
              )}
            </li>
          </ul>
          <div>
            {t(
              'To submit the order, Users must accept these Terms and use the respective button or mechanism on this Application, hereby committing to pay the agreed-upon price.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Order submission')}</div>
          <div>
            {t('When the User submits an order, the following applies')}
          </div>
          <ul>
            <li>
              {t(
                'The submission of an order determines contract conclusion and therefore creates for the User the obligation to pay the price, taxes and possible further fees and expenses, as specified on the order page.',
              )}
            </li>
            <li>
              {t(
                'In case the purchased Product requires active input from the User, such as the provision of personal information or data, specifications or special wishes, the order submission creates an obligation for the User to cooperate accordingly.',
              )}
            </li>
            <li>
              {t(
                'Upon submission of the order, Users will receive a receipt confirming that the order has been received.',
              )}
            </li>
          </ul>
          <div>
            {t(
              'All notifications related to the described purchasing process shall be sent to the email address provided by the User for such purposes.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Prices')}</div>
          <div>
            {t(
              'Users are informed during the purchasing process and before order submission, about any fees, taxes and costs (including, if any, delivery costs) that they will be charged.',
            )}
          </div>
          <div>{t('Prices on this Application are displayed')}</div>
          <ul>
            <li>
              {t(
                'either exclusive or inclusive of any applicable fees, taxes and costs, depending on the section the User is browsing.',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>{t('Methods of payment')}</div>
          <div>
            {t(
              'Information related to accepted payment methods are made available during the purchasing process.',
            )}
          </div>
          <div>
            {t(
              'Some payment methods may only be available subject to additional conditions or fees. In such cases related information can be found in the dedicated section of this Application.',
            )}
          </div>
          <div>
            {t(
              'All payments are independently processed through third-party services. Therefore, this Application does not collect any payment information – such as credit card details – but only receives a notification once the payment has been successfully completed.',
            )}
          </div>
          <div>
            {t(
              'If a payment through the available methods fails or is refused by the payment service provider, the Owner shall be under no obligation to fulfill the purchase order. If a payment fails or is refused, the Owner reserves the right to claim any related expenses or damages from the User.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Retention of usage rights')}
          </div>
          <div>
            {t(
              'Users do not acquire any rights to use the purchased Product until the total purchase price is received by the Owner.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Contractual right of cancellation')}
          </div>
          <div>
            {t(
              'The Owner grants Users a contractual right to cancel the purchase under the terms and conditions described in the relevant section of this Application within 30 days of concluding the contract.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Delivery')}</div>
          <div className={cnTerms('subheader')}>
            {t('Performance of services')}
          </div>
          <div>
            {t(
              'The purchased service shall be performed or made available within the timeframe specified on this Application or as communicated before the order submission.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Contract duration')}</div>
          <div className={cnTerms('subheader')}>{t('Trial period')}</div>
          <div>
            {t(
              'Users have the option to test this Application or selected Products during a limited and non-renewable trial period, at no cost. Some features or functions of this Application may not be available to Users during the trial period.Further conditions applicable to the trial period, including its duration, will be specified on this Application.',
            )}
          </div>
          <div>
            {t(
              'The trial period shall end automatically and shall not convert into any paid Product unless the User actively purchases such paid Product.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Subscriptions')}</div>
          <div>
            {t(
              'Subscriptions allow Users to receive a Product continuously or regularly over time. Details regarding the type of subscription and termination are outlined below.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Fixed-term subscriptions')}
          </div>
          <div>
            {t(
              'Paid fixed-term subscriptions start on the day the payment is received by the Owner and last for the subscription period chosen by the User or otherwise specified during the purchasing process.',
            )}
          </div>
          <div>
            {t(
              'Once the subscription period expires, the Product shall no longer be accessible.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Automatic renewal of fixed-term subscriptions')}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'Subscriptions are automatically renewed through the payment method that the User chose during purchase.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'The renewed subscription will last for a period equal to the original term.',
            )}
          </div>
          <div>
            {t(
              'The User shall receive a reminder of the upcoming renewal with reasonable advance, outlining the procedure to be followed in order to cancel the automatic renewal.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Termination')}</div>
          <div className={cnTerms('subheader')}>
            {t(
              'Subscriptions may be terminated by sending a clear and unambiguous termination notice to the Owner using the contact details provided in this document, or — if applicable — by using the corresponding controls inside this Application.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'If the notice of termination is received by the Owner before the subscription renews, the termination shall take effect as soon as the current period is completed.',
            )}
          </div>
          <div className={cnTerms('header')}>{t('User rights')}</div>
          <div className={cnTerms('subheader')}>{t('Right of withdrawal')}</div>
          <div>
            {t(
              'Unless exceptions apply, the User may be eligible to withdraw from the contract within the period specified below (generally 14 days), for any reason and without justification. Users can learn more about the withdrawal conditions within this section.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Who the right of withdrawal applies to')}
          </div>
          <div>
            {t(
              'Unless any applicable exception is mentioned below, Users who are European Consumers are granted a statutory cancellation right under EU rules, to withdraw from contracts entered into online (distance contracts) within the specified period applicable to their case, for any reason and without justification.',
            )}
          </div>
          <div>
            {t(
              'Users that do not fit this qualification, cannot benefit from the rights described in this section.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Exercising the right of withdrawal')}
          </div>
          <div>
            {t(
              'To exercise their right of withdrawal, Users must send to the Owner an unequivocal statement of their intention to withdraw from the contract.',
            )}
          </div>
          <div>
            {t(
              'To this end, Users may use the model withdrawal form available from within the “definitions” section of this document. Users are, however, free to express their intention to withdraw from the contract by making an unequivocal statement in any other suitable way. In order to meet the deadline within which they can exercise such right, Users must send the withdrawal notice before the withdrawal period expires.',
            )}
          </div>
          <div>{t('When does the withdrawal period expire?')}</div>
          <ul>
            <li>
              {t(
                'Regarding the purchase of a service, the withdrawal period expires 14 days after the day that the contract is entered into, unless the User has waived the withdrawal right.',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>
            {t('Effects of withdrawal')}
          </div>
          <div>
            {t(
              'Users who correctly withdraw from a contract will be reimbursed by the Owner for all payments made to the Owner, including, if any, those covering the costs of delivery.',
            )}
          </div>
          <div>
            {t(
              'However, any additional costs resulting from the choice of a particular delivery method other than the least expensive type of standard delivery offered by the Owner, will not be reimbursed.',
            )}
          </div>
          <div>
            {t(
              'Such reimbursement shall be made without undue delay and, in any event, no later than 14 days from the day on which the Owner is informed of the User’s decision to withdraw from the contract. Unless otherwise agreed with the User, reimbursements will be made using the same means of payment as used to process the initial transaction. In any event, the User shall not incur any costs or fees as a result of such reimbursement.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('…on the purchase of services')}
          </div>
          <div>
            {t(
              'Where a User exercises the right of withdrawal after having requested that the service be performed before the withdrawal period expires, the User shall pay to the Owner an amount which is in proportion to the part of service provided.',
            )}
          </div>
          <div>
            {t(
              'Such payment shall be calculated based on the fee contractually agreed upon, and be proportional to the part of service provided until the time the User withdraws, compared with the full coverage of the contract.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Contractual withdrawal period extension')}
          </div>
          <div>
            {t(
              'In addition, and without prejudice to the statutory right of withdrawal or any other statutory rights, including guarantee rights, the Owner grants Users a contractual right to withdraw from the contract within 30 days. Users can therefore withdraw from the contract even once the statutory withdrawal period has expired.',
            )}
          </div>
          <div>
            {t(
              'The contractual extension of the withdrawal period is subject to the same conditions stated for the statutory right of withdrawal, including those for determining the beginning and the end of the withdrawal period.',
            )}
          </div>
          <div className={cnTerms('header')}>
            {t('Liability and indemnification')}
          </div>
          <div className={cnTerms('subheader')}>{t('Indemnification')}</div>
          <div>
            {t(
              'The User agrees to indemnify and hold the Owner and its subsidiaries, affiliates, officers, directors, agents, co-branders, partners and employees harmless from and against any claim or demand ⁠— including but not limited to lawyers fees and costs ⁠— made by any third party due to or in relation with any culpable use of or connection to the Service, violation of these Terms, infringement of any third-party rights or statutory provision by the User or its affiliates, officers, directors, agents, co-branders, partners and employees to the extent allowed by applicable law.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Limitation of liability')}
          </div>
          <div>
            {t(
              'Unless otherwise explicitly stated and without prejudice to applicable statutory product liability provisions, Users shall have no right to claim damages against the Owner (or any natural or legal person acting on its behalf).',
            )}
          </div>
          <div>
            {t(
              'This does not apply to damages to life, health or physical integrity, damages resulting from the breach of an essential contractual obligation such as any obligation strictly necessary to achieve the purpose of the contract, and/or damages resulting from intent or gross negligence, as long as this Application has been appropriately and correctly used by the User.',
            )}
          </div>
          <div>
            {t(
              'Unless damages have been caused by way of intent or gross negligence, or they affect life, health or physical integrity, the Owner shall only be liable to the extent of typical and foreseeable damages at the moment the contract was entered into.',
            )}
          </div>
          <div>
            {t(
              'In particular, within the limits stated above, the Owner shall not be liable for',
            )}
          </div>
          <ul>
            <li>
              {t(
                'any loss of business opportunities and any other loss, even indirect, that may be incurred by the User (such as, but not limited to, trading losses, loss of revenue, income, profits or anticipated savings, loss of contracts or business relationships, loss of reputation or goodwill, etc.);',
              )}
            </li>
            <li>
              {t(
                'damages or losses resulting from interruptions or malfunctions of this Application due to acts of force majeure, or unforeseen and unforeseeable events and, in any case, independent of the will and beyond the control of the Owner, such as, but not limited to, failures or disruptions of telephone or electrical lines, the Internet and / or other means of transmission, unavailability of websites, strikes, natural disasters, viruses and cyber attacks, interruptions in the delivery of products, third-party services or applications;',
              )}
            </li>
            <li>
              {t(
                'any losses that are not the direct consequence of a breach of the Terms by the Owner;',
              )}
            </li>
            <li>
              {t(
                'any damage, prejudice or loss occurring due to viruses or other malware contained in or connected to files available for download from the internet or via this Application. Users are responsible for implementing sufficient security measures – such as anti-viruses and firewalls to prevent any such infection or attack and for securing backup copies of all data or information exchanged via or uploaded to this Application.',
              )}
            </li>
          </ul>
          <div>
            {t(
              'Notwithstanding the above, the following limitation applies to all Users not qualifying as Consumers',
            )}
          </div>
          <div>
            {t(
              'In any event of liability, the compensation may not exceed the total payments that have been, will be or would be received by the Owner from the User based on the contract over a period of 12 months, or the period of the duration of the Agreement, if shorter.',
            )}
          </div>
          <div className={cnTerms('header')}>{t('Australian Users')}</div>
          <div className={cnTerms('subheader')}>
            {t('Limitation of liability')}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'Nothing in these Terms excludes, restricts or modifies any guarantee, condition, warranty, right or remedy which the User may have under the Competition and Consumer Act 2010 (Cth) or any similar State and Territory legislation and which cannot be excluded, restricted or modified (non-excludable right). To the fullest extent permitted by law, our liability to the User, including liability for a breach of a non-excludable right and liability which is not otherwise excluded under these Terms of Use, is limited, at the Owner’s sole discretion, to the re-performance of the services or the payment of the cost of having the services supplied again.',
            )}
          </div>
          <div className={cnTerms('header')}>{t('US Users')}</div>
          <div className={cnTerms('subheader')}>
            {t('Disclaimer of Warranties')}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'This Application is provided strictly on an “as is” and “as available” basis. Use of the Service is at Users’ own risk. To the maximum extent permitted by applicable law, the Owner expressly disclaims all conditions, representations, and warranties — whether express, implied, statutory or otherwise, including, but not limited to, any implied warranty of merchantability, fitness for a particular purpose, or non-infringement of third-party rights. No advice or information, whether oral or written, obtained by user from owner or through the Service will create any warranty not expressly stated herein.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'Without limiting the foregoing, the Owner, its subsidiaries, affiliates, licensors, officers, directors, agents, co-branders, partners, suppliers and employees do not warrant that the content is accurate, reliable or correct; that the Service will meet Users’ requirements; that the Service will be available at any particular time or location, uninterrupted or secure; that any defects or errors will be corrected; or that the Service is free of viruses or other harmful components. Any content downloaded or otherwise obtained through the use of the Service is downloaded at users own risk and users shall be solely responsible for any damage to Users’ computer system or mobile device or loss of data that results from such download or Users’ use of the Service.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'The Owner does not warrant, endorse, guarantee, or assume responsibility for any product or service advertised or offered by a third party through the Service or any hyperlinked website or service, and the Owner shall not be a party to or in any way monitor any transaction between Users and third-party providers of products or services.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'The Service may become inaccessible or it may not function properly with Users’ web browser, mobile device, and/or operating system. The owner cannot be held liable for any perceived or actual damages arising from Service content, operation, or use of this Service.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'Federal law, some states, and other jurisdictions, do not allow the exclusion and limitations of certain implied warranties. The above exclusions may not apply to Users. This Agreement gives Users specific legal rights, and Users may also have other rights which vary from state to state. The disclaimers and exclusions under this agreement shall not apply to the extent prohibited by applicable law.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Limitations of liability')}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'To the maximum extent permitted by applicable law, in no event shall the Owner, and its subsidiaries, affiliates, officers, directors, agents, co-branders, partners, suppliers and employees be liable for',
            )}
          </div>
          <ul className={cnTerms('subheader')}>
            <li>
              {t(
                'any indirect, punitive, incidental, special, consequential or exemplary damages, including without limitation damages for loss of profits, goodwill, use, data or other intangible losses, arising out of or relating to the use of, or inability to use, the Service; and',
              )}
            </li>
            <li>
              {t(
                'any damage, loss or injury resulting from hacking, tampering or other unauthorized access or use of the Service or User account or the information contained therein;',
              )}
            </li>
            <li>{t('any errors, mistakes, or inaccuracies of content;')}</li>
            <li>
              {t(
                'personal injury or property damage, of any nature whatsoever, resulting from User access to or use of the Service;',
              )}
            </li>
            <li>
              {t(
                'any unauthorized access to or use of the Owner’s secure servers and/or any and all personal information stored therein;',
              )}
            </li>
            <li>
              {t(
                'any interruption or cessation of transmission to or from the Service;',
              )}
            </li>
            <li>
              {t(
                'any bugs, viruses, trojan horses, or the like that may be transmitted to or through the Service;',
              )}
            </li>
            <li>
              {t(
                'any errors or omissions in any content or for any loss or damage incurred as a result of the use of any content posted, emailed, transmitted, or otherwise made available through the Service; and/or',
              )}
            </li>
            <li>
              {t(
                'the defamatory, offensive, or illegal conduct of any User or third party. In no event shall the Owner, and its subsidiaries, affiliates, officers, directors, agents, co-branders, partners, suppliers and employees be liable for any claims, proceedings, liabilities, obligations, damages, losses or costs in an amount exceeding the amount paid by User to the Owner hereunder in the preceding 12 months, or the period of duration of this agreement between the Owner and User, whichever is shorter.',
              )}
            </li>
          </ul>
          <div className={cnTerms('subheader')}>
            {t(
              'This limitation of liability section shall apply to the fullest extent permitted by law in the applicable jurisdiction whether the alleged liability is based on contract, tort, negligence, strict liability, or any other basis, even if company has been advised of the possibility of such damage.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t(
              'Some jurisdictions do not allow the exclusion or limitation of incidental or consequential damages, therefore the above limitations or exclusions may not apply to User. The terms give User specific legal rights, and User may also have other rights which vary from jurisdiction to jurisdiction. The disclaimers, exclusions, and limitations of liability under the terms shall not apply to the extent prohibited by applicable law.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Indemnification')}</div>
          <div className={cnTerms('subheader')}>
            {t(
              'The User agrees to defend, indemnify and hold the Owner and its subsidiaries, affiliates, officers, directors, agents, co-branders, partners, suppliers and employees harmless from and against any and all claims or demands, damages, obligations, losses, liabilities, costs or debt, and expenses, including, but not limited to, legal fees and expenses, arising from',
            )}
          </div>
          <ul className={cnTerms('subheader')}>
            <li>
              {t(
                'User’s use of and access to the Service, including any data or content transmitted or received by User;',
              )}
            </li>
            <li>
              {t(
                'User’s violation of these terms, including, but not limited to, User’s breach of any of the representations and warranties set forth in these terms;',
              )}
            </li>
            <li>
              {t(
                'User’s violation of any third-party rights, including, but not limited to, any right of privacy or intellectual property rights;',
              )}
            </li>
            <li>
              {t('User’s violation of any statutory law, rule, or regulation;')}
            </li>
            <li>
              {t(
                'any content that is submitted from User’s account, including third party access with User’s unique username, password or other security measure, if applicable, including, but not limited to, misleading, false, or inaccurate information;',
              )}
            </li>
            <li>{t('User’s wilful misconduct; or')}</li>
            <li>
              {t(
                'statutory provision by User or its affiliates, officers, directors, agents, co-branders, partners, suppliers and employees to the extent allowed by applicable law.',
              )}
            </li>
          </ul>
          <div className={cnTerms('header')}>{t('Common provisions')}</div>
          <div className={cnTerms('subheader')}>{t('No Waiver')}</div>
          <div>
            {t(
              'The Owner’s failure to assert any right or provision under these Terms shall not constitute a waiver of any such right or provision. No waiver shall be considered a further or continuing waiver of such term or any other term.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Service interruption')}
          </div>
          <div>
            {t(
              'To ensure the best possible service level, the Owner reserves the right to interrupt the Service for maintenance, system updates or any other changes, informing the Users appropriately.',
            )}
          </div>
          <div>
            {t(
              'Within the limits of law, the Owner may also decide to suspend or terminate the Service altogether. If the Service is terminated, the Owner will cooperate with Users to enable them to withdraw Personal Data or information in accordance with applicable law.',
            )}
          </div>
          <div>
            {t(
              'Additionally, the Service might not be available due to reasons outside the Owner’s reasonable control, such as “force majeure” (eg. labor actions, infrastructural breakdowns or blackouts etc).',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Service reselling')}</div>
          <div>
            {t(
              'Users may not reproduce, duplicate, copy, sell, resell or exploit any portion of this Application and of its Service without the Owner’s express prior written permission, granted either directly or through a legitimate reselling programme.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Privacy policy')}</div>
          <div>
            {t(
              'For information about the use of their personal data, Users must refer to the privacy policy of this Application which is hereby declared to be part of these Terms.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Intellectual property rights')}
          </div>
          <div>
            {t(
              'Without prejudice to any more specific provision of these Terms, any intellectual property rights, such as copyrights, trademark rights, patent rights and design rights related to this Application are the exclusive property of the Owner or its licensors and are subject to the protection granted by applicable laws or international treaties relating to intellectual property.',
            )}
          </div>
          <div>
            {t(
              'All trademarks — nominal or figurative — and all other marks, trade names, service marks, word marks, illustrations, images, or logos appearing in connection with this Application are, and remain, the exclusive property of the Owner or its licensors and are subject to the protection granted by applicable laws or international treaties related to intellectual property.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Changes to these Terms')}
          </div>
          <div>
            {t(
              'The Owner reserves the right to amend or otherwise modify these Terms at any time. In such cases, the Owner will appropriately inform the User of these changes.',
            )}
          </div>
          <div>
            {t(
              'Such changes will only affect the relationship with the User for the future.',
            )}
          </div>
          <div>
            {t(
              'The continued use of the Service will signify the User’s acceptance of the revised Terms. If Users do not wish to be bound by the changes, they must stop using the Service. Failure to accept the revised Terms, may entitle either party to terminate the Agreement.',
            )}
          </div>
          <div>
            {t(
              'The applicable previous version will govern the relationship prior to the Users acceptance. The User can obtain any previous version from the Owner.',
            )}
          </div>
          <div>
            {t(
              'If required by applicable law, the Owner will specify the date by which the modified Terms will enter into force.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Assignment of contract')}
          </div>
          <div>
            {t(
              'The Owner reserves the right to transfer, assign, dispose of by novation, or subcontract any or all rights or obligations under these Terms, taking the User’s legitimate interests into account. Provisions regarding changes of these Terms will apply accordingly.',
            )}
          </div>
          <div>
            {t(
              'Users may not assign or transfer their rights or obligations under these Terms in any way, without the written permission of the Owner.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Contacts')}</div>
          <div>
            {t(
              'All communications relating to the use of this Application must be sent using the contact information stated in this document.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Severability')}</div>
          <div>
            {t(
              'Should any provision of these Terms be deemed or become invalid or unenforceable under applicable law, the invalidity or unenforceability of such provision shall not affect the validity of the remaining provisions, which shall remain in full force and effect.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('US Users')}</div>
          <div>
            {t(
              'Any such invalid or unenforceable provision will be interpreted, construed and reformed to the extent reasonably required to render it valid, enforceable and consistent with its original intent. These Terms constitute the entire Agreement between Users and the Owner with respect to the subject matter hereof, and supersede all other communications, including but not limited to all prior agreements, between the parties with respect to such subject matter. These Terms will be enforced to the fullest extent permitted by law.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('EU Users')}</div>
          <div>
            {t(
              'Should any provision of these Terms be or be deemed void, invalid or unenforceable, the parties shall do their best to find, in an amicable way, an agreement on valid and enforceable provisions thereby substituting the void, invalid or unenforceable parts.',
            )}
          </div>
          <div>
            {t(
              'In case of failure to do so, the void, invalid or unenforceable provisions shall be replaced by the applicable statutory provisions, if so permitted or stated under the applicable law.',
            )}
          </div>
          <div>
            {t(
              'Without prejudice to the above, the nullity, invalidity or the impossibility to enforce a particular provision of these Terms shall not nullify the entire Agreement, unless the severed provisions are essential to the Agreement, or of such importance that the parties would not have entered into the contract if they had known that the provision would not be valid, or in cases where the remaining provisions would translate into an unacceptable hardship on any of the parties.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Authoritative version of these Terms')}
          </div>
          <div>
            {t(
              'These Terms are drawn up and revised in Inglese. Other language versions of these Terms are provided for information purposes only. In the event of any inconsistency between different linguistic versions, the original version shall always prevail.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Governing law')}</div>
          <div>
            {t(
              'These Terms are governed by the law of the place where the Owner is based, as disclosed in the relevant section of this document, without regard to conflict of laws principles.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Exception for European Consumers')}
          </div>
          <div>
            {t(
              'However, regardless of the above, if the User qualifies as a European Consumer and has their habitual residence in a country where the law provides for a higher consumer protection standard, such higher standards shall prevail.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Venue of jurisdiction')}
          </div>
          <div>
            {t(
              'The exclusive competence to decide on any controversy resulting from or connected to these Terms lies with the courts of the place where the Owner is based, as displayed in the relevant section of this document.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Exception for European Consumers')}
          </div>
          <div>
            {t(
              'The above does not apply to any Users that qualify as European Consumers, nor to Consumers based in Switzerland, Norway or Iceland.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('UK Consumers')}</div>
          <div>
            {t(
              'Consumers based in England and Wales may bring legal proceedings in connection with these Terms in the English and Welsh courts. Consumers based in Scotland may bring legal proceedings in connection with these Terms in either the Scottish or the English courts. Consumers based in Northern Ireland may bring legal proceedings in connection with these Terms in either the Northern Irish or the English courts.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('US Users')}</div>
          <div>
            {t(
              'Each party specifically waives any right to trial by jury in any court in connection with any action or litigation.Any claims under these terms shall proceed individually and no party shall join in a class action or other proceeding with or on behalf of others.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('US Users')}</div>
          <div className={cnTerms('subheader')}>
            {t('Surviving provisions')}
          </div>
          <div>
            {t(
              'This Agreement shall continue in effect until it is terminated by either this Application or the User. Upon termination, the provisions contained in these Terms that by their context are intended to survive termination or expiration will survive, including but not limited to the following',
            )}
          </div>
          <ul>
            <li>
              {t(
                'the User’s grant of licenses under these Terms shall survive indefinitely;',
              )}
            </li>
            <li>
              {t(
                'the User’s indemnification obligations shall survive for a period of five years from the date of termination;',
              )}
            </li>
            <li>
              {t(
                'the disclaimer of warranties and representations, and the stipulations under the section containing indemnity and limitation of liability provisions, shall survive indefinitely.',
              )}
            </li>
          </ul>
          <div className={cnTerms('header')}>{t('Dispute resolution')}</div>
          <hr className={cnTerms('border')} />
          <div className={cnTerms('subheader')}>
            {t('Definitions and legal references')}
          </div>
          <div className={cnTerms('subheader')}>
            {t('This Application (or this Application)')}
          </div>
          <div>
            {t('The property that enables the provision of the Service.')}
          </div>
          <div className={cnTerms('subheader')}>{t('Agreement')}</div>
          <div>
            {t(
              'Any legally binding or contractual relationship between the Owner and the User, governed by these Terms.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Business User')}</div>
          <div>{t('Any User that does not qualify as a Consumer.')}</div>
          <div className={cnTerms('subheader')}>
            {t('European (or Europe)')}
          </div>
          <div>
            {t(
              'Applies where a User is physically present or has their registered offices within the EU, regardless of nationality.',
            )}
          </div>
          <div className={cnTerms('subheader')}>
            {t('Example withdrawal form')}
          </div>
          <div>{t('Addressed to:')}</div>
          <div>
            {t(
              'Dinamica Generale S.p.A. Via Mondadori, 15 - 46025 - Poggio Rusco (MN) company registration number 01599500202',
            )}
          </div>
          <div>{t('REA MN 169392 Paid-in capital 1.000.000,00 I.V.')}</div>
          <div>
            {t(
              'I/We hereby give notice that I/we withdraw from my/our contract of sale of the following goods/for the provision of the following service:',
            )}
          </div>
          <div>
            {t(
              '(insert a description of the goods/services that are subject to the respective withdrawal)',
            )}
          </div>
          <ul>
            <li>{t('Ordered on')}</li>
            <li>{t('Received on')}</li>
            <li>{t('Name of consumer(s)')}</li>
            <li>{t('Address of consumer(s)')}</li>
            <li>{t('Date')}</li>
          </ul>
          <div>{t('(sign if this form is notified on paper)')}</div>
          <div className={cnTerms('subheader')}>{t('Owner (or We)')}</div>
          <div>
            {t(
              'Indicates the natural person(s) or legal entity that provides this Application and/or the Service to Users.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Product')}</div>
          <div>
            {t(
              'A good or service available for purchase through this Application, such as e.g. physical goods, digital files, software, booking services etc.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Service')}</div>
          <div>
            {t(
              'The service provided by this Application as described in these Terms and on this Application.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Terms')}</div>
          <div>
            {t(
              'All provisions applicable to the use of this Application and/or the Service as described in this document, including any other related documents or agreements, and as updated from time to time.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('User (or You)')}</div>
          <div>
            {t(
              'Indicates any natural person or legal entity using this Application.',
            )}
          </div>
          <div className={cnTerms('subheader')}>{t('Consumer')}</div>
          <div>
            {t(
              'Any User qualifying as a natural person who accesses goods or services for personal use, or more generally, acts for purposes outside their trade, business, craft or profession.',
            )}
          </div>
          <hr className={cnTerms('border')} />
          <div>{t('Latest update: April 21, 2023')}</div>
          <div>
            {t(
              'iubenda hosts this content and only collects the Personal Data strictly necessary for it to be provided.',
            )}
          </div>
        </div>
      </div>
    </>
  )
})
